import { useQuery } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { toWei } from 'web3-utils'
import { ResultOrError } from '@lukso/controllerapps-sharedlogic-ddd-core'

import { ControllerDTO } from '@/business-logic/profile'
import { InternalMethods } from '@/shared/apis/messages'
import { useGlobals } from '../compositions/useGlobals'
import { useAppStore } from './AppStore'

export const useProfileStore = defineStore('profile', {
  state: () => ({
    controllers: ref<ControllerDTO[]>(),
    deviceController: ref<ControllerDTO | undefined>(),
  }),
  getters: {
    getControllerByAddress:
      state =>
      (address: string): ControllerDTO | undefined => {
        return state.controllers?.find(
          controller => controller.address === address
        )
      },
    // eslint-disable-next-line unicorn/consistent-function-scoping
    getControllerBalance: () => (controllerAddress: string) => {
      const { isPending, isError, data } = useQuery({
        queryKey: ['controller-balance'],
        refetchInterval: 5000,
        queryFn: async () => {
          const balance = await useGlobals().sendMessage({
            method: InternalMethods.GET_CONTROLLER_BALANCE,
            payload: {
              controllerAddress,
            },
          })
          return toWei(balance)
        },
      })
      return { isBalancePendingOrError: isPending || isError, balance: data }
    },
  },
  actions: {
    async initialize() {
      const appStore = useAppStore()
      watch(
        () => appStore.selectedProfile,
        this.fetchControllers,
        // Run on initial setup
        { immediate: true }
      )
    },
    async fetchControllers() {
      const appStore = useAppStore()
      if (!appStore.selectedProfile) {
        this.controllers = []
        this.deviceController = undefined
        return
      }
      this.controllers = await useGlobals().sendMessage({
        method: InternalMethods.GET_CONTROLLERS,
        payload: {
          profileAddress: appStore.selectedProfile,
        },
      })
      this.deviceController = await useGlobals().sendMessage({
        method: InternalMethods.GET_UP_BROWSER_EXTENSION_CONTROLLER,
        payload: {
          profileAddress: appStore.selectedProfile,
        },
      })
    },
    async saveCustomControllerName(newName: string, controllerAddress: string) {
      const appStore = useAppStore()
      if (!appStore.selectedProfile) {
        return ResultOrError.error()
      }
      const resultOrError = await useGlobals().sendMessage({
        method: InternalMethods.SAVE_CUSTOM_CONTROLLER_NAME,
        payload: {
          profileAddress: appStore.selectedProfile,
          controllerAddress,
          name: newName,
        },
      })
      await this.fetchControllers()
      return resultOrError
    },
  },
})
